/**
 * useQualificationTags — hydrates the editable tag lists once and
 * shares them across all screens that render the picker chips
 * (LeadNew, LeadEdit, TechnicalQualification, PublicEstimate summary…).
 *
 * The backend endpoint ``GET /api/qualification-tags`` returns two
 * buckets: ``service_types`` and ``needs``. Each tag has ``value`` (the
 * canonical string stored on the lead), ``label`` (what we render) and
 * ``active`` (false → deactivated, hide completely per user directive).
 *
 * We only expose ``active`` tags to the pickers — this means old
 * leads that still carry a deactivated tag will visually skip that
 * chip (which matches the user's decision: "esconder completamente").
 * If the caller ever needs the full list (admin settings), pass
 * ``includeInactive: true``.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

// Small in-memory cache so multiple concurrent components share
// the same fetch. Refresh happens on ``invalidate()`` from the
// admin settings page.
let _cachePromise = null;
let _cacheValue = null;
const _subscribers = new Set();

function _notify() {
  for (const cb of _subscribers) {
    try { cb(_cacheValue); } catch (e) { /* ignore */ }
  }
}

async function _fetch() {
  const r = await api.get("/qualification-tags");
  _cacheValue = r.data || { service_types: [], needs: [] };
  _notify();
  return _cacheValue;
}

export function invalidateQualificationTagsCache() {
  _cachePromise = null;
  _cacheValue = null;
  return _fetch();
}

export function useQualificationTags({ includeInactive = false } = {}) {
  const [state, setState] = useState(_cacheValue);
  useEffect(() => {
    const cb = (v) => setState(v);
    _subscribers.add(cb);
    if (!_cachePromise) {
      _cachePromise = _fetch().catch((e) => {
        _cachePromise = null;
        throw e;
      });
    }
    _cachePromise.then((v) => setState(v)).catch(() => {});
    return () => { _subscribers.delete(cb); };
  }, []);

  const filter = (list) => (list || [])
    .filter((t) => includeInactive || t.active !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return {
    loading: !state,
    serviceTypes: filter(state?.service_types),
    needs: filter(state?.needs),
    // Convenience: array of just the ``value`` strings (matches the old
    // hardcoded SERVICE_TYPES / NEEDS import pattern in LeadNew).
    serviceTypeValues: filter(state?.service_types).map((t) => t.value),
    needsValues: filter(state?.needs).map((t) => t.value),
    refresh: invalidateQualificationTagsCache,
  };
}
